import { useEffect, useState } from 'react'
import { useMedplum, useMedplumProfile } from '@medplum/react'
import type { Practitioner, PractitionerRole } from '@medplum/fhirtypes'
import { logError } from '@/utils/errorUtils'
import { SNOMED_USER_ROLES } from '@/utils'

const EXCLUDED_ROLE_CODES = [SNOMED_USER_ROLES.HOSPITAL_MANAGER.code, '224579007']

export type CareCoordinatorRoleStatus = 'loading' | 'ready' | 'error'

export type CareCoordinatorRoleState = {
  status: CareCoordinatorRoleStatus
  isCareCoordinator: boolean
  practitioners: Practitioner[]
}

async function fetchOrgPractitioners(
  medplum: ReturnType<typeof useMedplum>,
  role: PractitionerRole,
): Promise<Practitioner[]> {
  const result = await medplum.search('PractitionerRole', {
    organization: role.organization?.reference,
    _include: 'PractitionerRole:practitioner',
    _count: 500,
    active: 'true',
  })

  const entries = result?.entry ?? []

  const rolesById = new Map<string, PractitionerRole>()
  const practitionersById = new Map<string, Practitioner>()

  for (const entry of entries) {
    const resource = entry.resource
    if (resource?.resourceType === 'PractitionerRole' && resource.practitioner?.reference) {
      rolesById.set(resource.practitioner.reference, resource)
    } else if (resource?.resourceType === 'Practitioner' && resource.id) {
      practitionersById.set(`Practitioner/${resource.id}`, resource)
    }
  }

  return Array.from(practitionersById.entries())
    .filter(([ref]) => {
      const roleCode = rolesById.get(ref)?.code?.[0]?.coding?.[0]?.code ?? ''
      return !EXCLUDED_ROLE_CODES.includes(roleCode)
    })
    .map(([, practitioner]) => practitioner)
}

export function useCareCoordinatorRole(): CareCoordinatorRoleState {
  const medplum = useMedplum()
  const profile = useMedplumProfile()

  const [state, setState] = useState<CareCoordinatorRoleState>({
    status: 'loading',
    isCareCoordinator: false,
    practitioners: [],
  })

  useEffect(() => {
    let cancelled = false
    const profileId = profile?.resourceType === 'Practitioner' ? profile.id : undefined

    if (!profileId) {
      setState({ status: 'ready', isCareCoordinator: false, practitioners: [] })
      return
    }

    setState({ status: 'loading', isCareCoordinator: false, practitioners: [] })

    async function check() {
      try {
        // **Every role row, and every coding within it.** `searchOne` read only the first row and
        // `code[0].coding[0]` only the first coding, so a coordinator who also holds a clinician
        // role was answered by whichever row came back first — sometimes coordinator, sometimes
        // not. The middleware accepts the role from ANY row (`ZocdocOpsGuard`), and a UI that
        // disagrees hides controls the server would have allowed.
        const roles = await medplum.searchResources(
          'PractitionerRole',
          `practitioner=Practitioner/${profileId}`,
        )

        const coordinatorRole = roles.find((entry) =>
          entry.code?.some((code) =>
            code.coding?.some((coding) => coding.code === SNOMED_USER_ROLES.HOSPITAL_MANAGER.code),
          ),
        )
        const isCareCoordinator = Boolean(coordinatorRole)

        // The coordinator row specifically, not whichever came first: this reads the
        // organization the coordinator belongs to.
        const practitioners = coordinatorRole
          ? await fetchOrgPractitioners(medplum, coordinatorRole)
          : []

        if (!cancelled) {
          setState({ status: 'ready', isCareCoordinator, practitioners })
        }
      } catch (err) {
        if (!cancelled) {
          logError('care-coordinator-role', 'Failed to fetch PractitionerRole', err)
          setState({ status: 'error', isCareCoordinator: false, practitioners: [] })
        }
      }
    }

    void check()
    return () => {
      cancelled = true
    }
  }, [medplum, profile])

  return state
}
